import { useContext, useEffect, useRef, useState } from "react";
import { RoomCacheContext } from "../../context/roomContext";
import type { RoomContext } from "../../context/types";

export function useMeetingConfig(connectId: string) {
  const { setRoomCache, setMeetingConfig, roomCache, meetingConfig } =
    useContext(RoomCacheContext) as RoomContext;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sdkKey = (process.env.REACT_APP_ZOOM_SDK_KEY || "").trim();
  const startedRef = useRef(false);

  useEffect(() => {
    // If already loaded, don't refetch
    if (roomCache && meetingConfig) return;

    // Fail loudly instead of silently returning
    if (!connectId) {
      setError("Missing connectId in URL.");
      return;
    }
    if (!sdkKey) {
      setError("Missing REACT_APP_ZOOM_SDK_KEY at build time (staging env not injected).");
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch("/api/getRoomCacheConnectId", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ connectId }),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`getRoomCacheConnectId failed (${res.status}): ${text.slice(0, 200)}`);
        }

        // If the server accidentally returns HTML, this will be obvious now.
        const text = await res.text();
        let data: any;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error(`API returned non-JSON (first 120 chars): ${text.slice(0, 120)}`);
        }

        if (!data?.roomCache?.[0]) throw new Error("No roomCache returned");

        const rc = data.roomCache[0];
        const password = rc.appointmentId?.split("-")?.[1] || "";
        const role = data.role;
        const signature = data.signature;

        const name =
          role === 1
            ? rc.doctorName
            : (() => {
                const parts = (rc.patientName || "").split(",");
                return parts.length === 2 ? `${parts[1]} ${parts[0]}` : rc.patientName;
              })();

        setRoomCache(rc);
        setMeetingConfig({
          sdkKey,
          webEndpoint: "zoom.us",
          signature,
          topic: rc.roomCacheId,
          name,
          password,
          role,
        });
      } catch (e: any) {
        startedRef.current = false; // allow retry if they navigate back
        setError(e?.message || "Failed to load meeting config");
      } finally {
        setLoading(false);
      }
    })();
  }, [connectId, sdkKey, roomCache, meetingConfig, setMeetingConfig, setRoomCache]);

  return { loading, error };
}
